import { GetScanResponse } from '../../api/scans';
import { GetMemberResponse } from '../../types/apiGeneratedTypes';
import {
  ConsolidatedSystemBlock,
  FbsReportData,
  cleanLegacyConclusionText,
  filterSystemsByStatuses,
  findingCountsByStatus,
  FOLLOW_UP_DISPLAY_LABELS,
  FOLLOW_UP_REMARK_LABELS,
  formatScanDateTimeLine,
  getConsolidatedSystemsData,
  getConsolidatedStatusRowClass,
  getFindingFilters,
  normalizeFollowUpKey,
  pickBoolOrStringAcrossScanSources,
  pickFollowUpRemarkCandidate,
  pickStringAcrossScanSources,
  resolveUsesFbsFindingStatusV2
} from './fullBodyScanReportUtils';

export type StatusTagVariant =
  | 'important'
  | 'critical'
  | 'incidental'
  | 'significant'
  | 'inconclusive'
  | 'no-abnormality'
  | 'no-findings'
  | 'not-reported'
  | 'seen'
  | 'benign'
  | 'default';

export interface ConsolidatedReportModel {
  bookingUsesV2: boolean;
  patientName: string;
  scanDateTimeLine: string;
  dateOfBirth: string;
  bookingId: string;
  gender: string;
  radiologist: string;
  gp: string;
  followUpLabel: string;
  conclusionBody: string;
  conclusionEmptyMessage: string;
  technique: string;
  scanTechnical: string;
  scanCoverage: string;
  additionalComments: string;
  filterSummary: string;
  findingCounts: Record<string, number>;
  filteredSystems: ConsolidatedSystemBlock[];
}

const formatDateGb = (value?: string): string => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('en-GB');
};

const pick = (obj: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const v = obj[k];
    if (v != null && String(v).trim() !== '') return String(v);
  }
  return '';
};

const formatGenderLabel = (value?: string | null): string => {
  if (value == null || String(value).trim() === '' || String(value) === '-') return '-';
  const s = String(value);
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
};

export function statusTagVariantFromClass(className: string): StatusTagVariant {
  const map: Record<string, StatusTagVariant> = {
    'consolidated-tag-important': 'important',
    'consolidated-tag-critical': 'critical',
    'consolidated-tag-incidental': 'incidental',
    'consolidated-tag-significant': 'significant',
    'consolidated-tag-inconclusive': 'inconclusive',
    'consolidated-tag-no-abnormality': 'no-abnormality',
    'consolidated-tag-no-findings': 'no-findings',
    'consolidated-tag-not-reported': 'not-reported',
    'consolidated-tag-seen': 'seen',
    'consolidated-tag-benign': 'benign',
    'consolidated-tag-default': 'default'
  };
  return map[className] || 'default';
}

export function statusTagVariant(status?: string, bookingUsesV2 = false): StatusTagVariant {
  return statusTagVariantFromClass(getConsolidatedStatusRowClass(status, bookingUsesV2));
}

export interface BuildConsolidatedReportModelInput {
  scanData: GetScanResponse;
  reportData: FbsReportData;
  member?: GetMemberResponse | null;
  selectedStatuses?: string[];
}

export function buildConsolidatedReportModel({
  scanData,
  reportData,
  member,
  selectedStatuses
}: BuildConsolidatedReportModelInput): ConsolidatedReportModel {
  const raw = scanData as Record<string, unknown>;
  const bookingUsesV2 = resolveUsesFbsFindingStatusV2(raw, reportData);
  const findingFilters = getFindingFilters(bookingUsesV2);
  const activeFilterIds = selectedStatuses ?? findingFilters.map((f) => f.id);
  const systemsData = getConsolidatedSystemsData(reportData, bookingUsesV2);
  const counts = findingCountsByStatus(systemsData, bookingUsesV2);
  const filteredSystems = filterSystemsByStatuses(systemsData, activeFilterIds, bookingUsesV2);

  const followKey = normalizeFollowUpKey(pickFollowUpRemarkCandidate(raw));
  const conclusionsRaw = pickStringAcrossScanSources(raw, [
    'conclusions_and_recommendations',
    'conclusionsAndRecommendations',
    'conclusion',
    'conclusions',
    'clinical_conclusions',
    'clinicalConclusions'
  ]);
  const normalizedConclusions = cleanLegacyConclusionText(conclusionsRaw, [
    FOLLOW_UP_DISPLAY_LABELS[followKey] || '',
    FOLLOW_UP_REMARK_LABELS[followKey] || ''
  ].filter(Boolean));
  const conclusionBody = normalizedConclusions || conclusionsRaw;

  const patientFirst =
    (member?.first_name && member.first_name.trim()) ||
    pick(raw, ['patient_first_name', 'first_name', 'firstName', 'member_first_name']);
  const patientLast =
    (member?.last_name && member.last_name.trim()) ||
    pick(raw, ['patient_last_name', 'last_name', 'lastName', 'member_last_name']);
  const bookingNum = pick(raw, ['booking_number', 'booking_id', 'number']);
  const genderRaw = pick(raw, ['gender', 'patient_gender']);
  const gender =
    member?.gender != null && String(member.gender).trim() !== ''
      ? formatGenderLabel(String(member.gender))
      : formatGenderLabel(genderRaw || null);

  const filterSummary =
    activeFilterIds.length === 0 || activeFilterIds.length === findingFilters.length
      ? 'Showing all findings'
      : findingFilters
          .filter((f) => activeFilterIds.includes(f.id))
          .map((f) => f.label)
          .join(', ');

  let conclusionEmptyMessage = 'No conclusions or follow-up recommendation recorded.';
  if (followKey && !conclusionBody) {
    conclusionEmptyMessage = 'No conclusion text on file.';
  }

  return {
    bookingUsesV2,
    patientName: [patientFirst, patientLast].filter(Boolean).join(' ') || '-',
    scanDateTimeLine: formatScanDateTimeLine(raw),
    dateOfBirth: formatDateGb(
      ((member?.dob != null && String(member.dob).trim()) ||
        pick(raw, ['patient_dob', 'dob', 'date_of_birth'])) as string | undefined
    ),
    bookingId: bookingNum ? `#${bookingNum}` : '-',
    gender,
    radiologist: pick(raw, ['radiologist_name', 'radiologistName']) || '-',
    gp: pick(raw, ['gp_name', 'gpName']) || '-',
    followUpLabel: followKey ? FOLLOW_UP_DISPLAY_LABELS[followKey] || followKey : '',
    conclusionBody,
    conclusionEmptyMessage,
    technique:
      pickStringAcrossScanSources(raw, [
        'technique',
        'imaging_technique',
        'imagingTechnique',
        'radiologist_technique',
        'radiologistTechnique'
      ]) || '-',
    scanTechnical:
      pickBoolOrStringAcrossScanSources(raw, [
        'scan_technical_satisfactory_note',
        'scanTechnicalSatisfactoryNote',
        'scan_technical_satisfactory',
        'scanTechnicalSatisfactory'
      ]) || '-',
    scanCoverage:
      pickBoolOrStringAcrossScanSources(raw, [
        'scan_adequate_coverage_note',
        'scanAdequateCoverageNote',
        'scan_adequate_coverage',
        'scanAdequateCoverage'
      ]) || '-',
    additionalComments:
      pickStringAcrossScanSources(raw, ['additional_comments', 'additionalComments']) || '-',
    filterSummary,
    findingCounts: counts,
    filteredSystems
  };
}
